import { get } from '../api/ajax';
import { getOrderSummary, getSettings } from '../selectors/selectors';
import doMetric from '../tracking/bigQueryMetricRecorder';
import MessageType from '../../enums/MessageType';
import setEditBasketValidationMessage from './OptionalBasketItemActions/setEditBasketValidationMessage';
import Track from '../utils/tracking';

/**
 * Validates basket item changes between current and new order summary
 * Checks if changes are legitimate based on optional basket items configuration
 * 
 * @param {Object} state - Redux state
 * @param {Function} dispatch - Redux dispatch function
 * @param {Object} newOrderSummary - New order summary to validate against current state
 */
export default function validateBasketItemChanges(state, dispatch, newOrderSummary) {
  // Get the current order summary from state to compare with the new one
  const currentOrderSummary = getOrderSummary(state);
  
  if (!currentOrderSummary || !currentOrderSummary.basketItems || !newOrderSummary.basketItems) {
    return;
  }
  
  // Extract product IDs from current and new basket items
  const currentProductIds = new Set(currentOrderSummary.basketItems.map(item => item.productId));
  const newProductIds = new Set(newOrderSummary.basketItems.map(item => item.productId));
  
  // Get optional basket items from the new order summary
  const optionalBasketItems = newOrderSummary.optionalBasketItems || [];
  
  // Find products that are in new order summary but not in current (added products)
  const addedProductIds = [...newProductIds].filter(productId => !currentProductIds.has(productId));
  
  // Find products that are in current order summary but not in new (removed products)
  const removedProductIds = [...currentProductIds].filter(productId => !newProductIds.has(productId));
  
  // Check if added products are legitimate (exist in optional basket items with isAdded=true)
  const unexpectedAddedProducts = addedProductIds.filter(productId => {
    const optionalItem = optionalBasketItems.find(item => item.productId === productId);
    return !optionalItem || !optionalItem.isAdded;
  });
  
  // Check if removed products are legitimate (exist in optional basket items with isAdded=false)
  const unexpectedRemovedProducts = removedProductIds.filter(productId => {
    const optionalItem = optionalBasketItems.find(item => item.productId === productId);
    return !optionalItem || optionalItem.isAdded !== false;
  });
  
  // If there are unexpected changes, log metrics for monitoring
  if (unexpectedAddedProducts.length > 0 || unexpectedRemovedProducts.length > 0) {
    const optionalBasketItemProductIds = optionalBasketItems.map(item => item.productId).filter(id => id !== undefined);
    
    try {
      doMetric(
        'basketItemMismatchDetected',
        [
          {name: 'oldProductIds', value: JSON.stringify([...currentProductIds])},
          {name: 'newProductIds', value: JSON.stringify([...newProductIds])},
          {name: 'unexpectedNewProductIds', value: JSON.stringify(unexpectedAddedProducts)},
          {name: 'unexpectedMissingProductIds', value: JSON.stringify(unexpectedRemovedProducts)},
          {name: 'optionalBasketItemProductIds', value: JSON.stringify(optionalBasketItemProductIds)},
        ], [], 'impression'
      );
    } catch (error) {
      Track.error('Error logging basket item mismatch:', error);  
    }
    
    if(getSettings(state)?.show_editable_basket_item_mismatch_warning) {
      dispatch(setEditBasketValidationMessage({
        messageTranslationKey: 'basket.edit.unexpected.basket.item.changes',
        messageType: MessageType.WARNING,
      }));
    }
  }
}